import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Minus, Plus, ShoppingCart, ArrowLeft, Truck, ShieldCheck, Scissors } from "lucide-react";
import { toast } from "sonner";
import { api, formatEUR } from "@/lib/api";
import { getSheetInfo } from "@/lib/materials";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";

export default function ProductPage() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setQty(1);
    api.get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        return api.get("/products", { params: { category: r.data.category } });
      })
      .then((r) => setRelated(r.data.filter((p) => p.id !== id).slice(0, 4)))
      .catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center" data-testid="product-not-found">
        <h1 className="font-display text-2xl font-bold text-[#1A1D1A]">Izdelek ne obstaja</h1>
        <Link to="/trgovina" className="mt-4 inline-block text-[#B45309] font-semibold hover:underline">Nazaj v trgovino</Link>
      </div>
    );
  }

  if (!product) {
    return <p className="text-center text-stone-500 py-24" data-testid="product-loading">Nalagam izdelek …</p>;
  }

  const discount = product.old_price
    ? Math.round((1 - product.price / product.old_price) * 100)
    : null;
  const sheet = getSheetInfo(product);

  const handleAdd = () => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      unit: product.unit,
      qty,
    });
    toast.success("Dodano v košarico", { description: `${qty} × ${product.name}` });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="product-page">
      <Link to="/trgovina" data-testid="back-to-catalog-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone-500 hover:text-[#B45309] transition-colors mb-8">
        <ArrowLeft className="w-4 h-4" /> Nazaj v trgovino
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        <div className="relative rounded-2xl overflow-hidden bg-[#F3EFE6] aspect-[4/3]">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          {discount && (
            <span className="absolute top-4 left-4 bg-[#D97706] text-white text-sm font-bold px-3 py-1.5 rounded-full font-tech" data-testid="product-discount-badge">
              −{discount}%
            </span>
          )}
        </div>

        <div>
          {product.badge && (
            <span className="inline-block bg-[#2D4030] text-[#FBF9F5] text-xs font-semibold px-3 py-1 rounded-full mb-4">{product.badge}</span>
          )}
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1D1A] leading-snug" data-testid="product-name">
            {product.name}
          </h1>
          <p className="mt-3 font-tech text-sm text-stone-500">{product.dimensions}</p>

          <div className="mt-6 flex items-baseline gap-3 flex-wrap">
            <span className="font-tech font-bold text-3xl text-[#1A1D1A]" data-testid="product-price">
              {formatEUR(sheet ? sheet.unitPrice : product.price)}
            </span>
            {(sheet ? sheet.oldUnitPrice : product.old_price) && (
              <span className="font-tech text-lg text-stone-400 line-through">
                {formatEUR(sheet ? sheet.oldUnitPrice : product.old_price)}
              </span>
            )}
            <span className="text-sm text-stone-500">{sheet ? (sheet.mode === "tm" ? "/ tekoči meter, z DDV" : "/ m², z DDV") : `/ ${product.unit}, z DDV`}</span>
          </div>
          {sheet && (
            <p className="mt-2 text-xs text-stone-500" data-testid="product-m2-note">
              {sheet.mode === "tm"
                ? `Obračun na tekoči meter · nabavna enota: cela plošča (${product.dimensions}, ${sheet.lengthM.toFixed(2)} tm) = ${formatEUR(product.price)}`
                : `Obračun na m² · nabavna enota: cela plošča (${product.dimensions}, ${sheet.areaM2.toFixed(2)} m²) = ${formatEUR(product.price)}`}
            </p>
          )}

          <p className="mt-6 text-stone-600 leading-relaxed">{product.desc}</p>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center gap-1 bg-white border border-[#E4E0D7] rounded-full px-1 h-12">
              <button data-testid="qty-minus-btn" onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 flex items-center justify-center text-stone-600 hover:text-[#B45309]">
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-8 text-center font-tech font-bold" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-plus-btn" onClick={() => setQty(qty + 1)} className="w-10 h-10 flex items-center justify-center text-stone-600 hover:text-[#B45309]">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              data-testid="product-add-to-cart-btn"
              onClick={handleAdd}
              className="flex-1 h-12 rounded-full bg-[#B45309] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#92400e] transition-colors"
            >
              <ShoppingCart className="w-4 h-4" /> Dodaj v košarico
            </button>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { icon: Truck, t: "Dostava 48 h" },
              { icon: ShieldCheck, t: "Garancija 2 leti" },
              { icon: Scissors, t: "Možen razrez" },
            ].map((f) => (
              <div key={f.t} className="flex items-center gap-2 bg-white border border-[#E4E0D7] rounded-xl px-3 py-2.5">
                <f.icon className="w-4 h-4 text-[#B45309] shrink-0" />
                <span className="text-xs font-semibold text-[#1A1D1A]">{f.t}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-white rounded-2xl border border-[#E4E0D7] p-5" data-testid="product-specs">
            <h3 className="font-display font-bold text-[#1A1D1A] mb-3">Tehnične lastnosti</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-stone-500">Šifra</dt><dd className="font-tech font-semibold uppercase">{product.id}</dd></div>
              <div className="flex justify-between"><dt className="text-stone-500">Dimenzije</dt><dd className="font-tech">{product.dimensions}</dd></div>
              {product.decor && <div className="flex justify-between"><dt className="text-stone-500">Dekor</dt><dd>{product.decor}</dd></div>}
              {product.thickness && <div className="flex justify-between"><dt className="text-stone-500">Debelina</dt><dd className="font-tech">{product.thickness} mm</dd></div>}
              <div className="flex justify-between"><dt className="text-stone-500">Enota</dt><dd>{product.unit}</dd></div>
            </dl>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-20">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1D1A] mb-8">Podobni izdelki</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
